import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  styled,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { getApiCall, postApiCall } from "../../../../utils/api";
import Swal from "sweetalert2";

const DropZone = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff4e6",
  padding: theme.spacing(5),
  textAlign: "center",
  cursor: "pointer",
  borderRadius: 8,
  border: "2px dashed #ff9900",
}));

const SchoolBulk = ({ setIsOpen, fetchData }) => {
  const [validData, setValidData] = useState([]);
  const [invalidCount, setInvalidCount] = useState(0);

  const [schoolData, setSchoolData] = useState([]);
  const [schoolId, setSchoolId] = useState("");

  const processData = (data) => {
    let valid = [];
    let invalid = 0;

    // Assume first row is header
    data.slice(1).forEach((row, index) => {
      const [
        schoolName,
        schoolType,
        schoolAddress,
        schoolPinCode,
        schoolState,
        schoolCity,
        schoolPhone,
        schoolEmail,
        coordinatorName,
        coordinatorDesignation,
        coordinatorPhone,
        coordinatorEmail,
      ] = row;

      if (
        schoolName?.trim() &&
        schoolType?.trim() &&
        schoolAddress?.trim() &&
        schoolPinCode?.toString().trim() &&
        schoolState?.trim() &&
        schoolCity?.trim()
      ) {
        valid.push({
          schoolName: schoolName.trim(),
          schoolType: schoolType.trim(),
          schoolAddress: schoolAddress.trim(),
          schoolPinCode: schoolPinCode.toString().trim(),
          schoolState: schoolState.trim(),
          schoolCity: schoolCity.trim(),
          schoolPhone: schoolPhone ? schoolPhone.toString().trim() : "",
          schoolEmail: schoolEmail ? schoolEmail.toString().trim() : "",
          coordinatorName: coordinatorName ? coordinatorName.trim() : "",
          coordinatorDesignation: coordinatorDesignation
            ? coordinatorDesignation.trim()
            : "",
          coordinatorPhone: coordinatorPhone
            ? coordinatorPhone.toString().trim()
            : "",
          coordinatorEmail: coordinatorEmail
            ? coordinatorEmail.toString().trim()
            : "",
        });
      } else {
        invalid++;
      }
    });

    setValidData(valid);
    setInvalidCount(invalid);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    console.log("123", fileExtension);

    if (fileExtension === "csv") {
      Papa.parse(file, {
        skipEmptyLines: true,
        complete: function (results) {
          processData(results.data);
        },
      });
    } else if (fileExtension === "xlsx") {
      const reader = new FileReader();
      reader.onload = function (event) {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        processData(parsedData);
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert("Please upload a CSV or XLSX file");
    }
  };

  const handleCancel = () => {
    setValidData([]);
    setInvalidCount(0);
    if (setIsOpen) setIsOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const requestData = validData.map((formData) => ({
        school: {
          name: formData.schoolName,
          description: `${formData.schoolName}'s Description`,
          image:
            "https://firebasestorage.googleapis.com/v0/b/snipe-nrsm.appspot.com/o/569241.png?alt=media&token=d8c9d5b8-d1cd-4f47-82ae-c9384c6fe6db",
          class_numbers: [-2, -1, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
          section_count: 1,
          model: "DIGITAL",
          address:
            formData.schoolAddress +
            ", " +
            (formData.schoolCity === "Other"
              ? formData.otherCity
              : formData.schoolCity) +
            ", " +
            formData.schoolState,
          pincode: formData.schoolPinCode
            ? parseInt(formData.schoolPinCode)
            : undefined,
          state_code: formData.schoolState,
          phone: formData.schoolPhone,
          email: formData.schoolEmail,
          coordinator_designation: formData.coordinatorDesignation,
          city:
            formData.schoolCity === "Other"
              ? formData.otherCity
              : formData.schoolCity,
          schoolType: formData.schoolType,
        },
        admin: {
          name: formData.coordinatorName,
          phone: formData.coordinatorPhone,
          email: formData.coordinatorEmail,
        },
      }));

      console.log("Request Data", requestData.school);
      const res = await postApiCall(
        "school/create-new-school-with-admin-bulk",
        {
          bulk_school_upload_data: requestData,
        }
      );

      if (res.success === false) {
        if (setIsOpen) setIsOpen(false);

        return Swal.fire({
          title: "Error",
          text: res.message,
          icon: "error",
        });
      }

      Swal.fire({
        title: "Successful",
        text: res.message,
        icon: "success",
      });

      fetchData();
      if (setIsOpen) setIsOpen(false);
    } catch (err) {
      console.error("Submission error:", err);
      Swal.fire({
        title: "Error",
        text: "Something went wrong while submitting.",
        icon: "error",
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography sx={{ mb: 1 }}>
        Tip:{" "}
        <Link
          href="/NRSM_School_Upload_Sample.xlsx"
          download
          underline="hover"
          color="orange"
        >
          Download Sample File Here (Do not remove the header from the sample
          file)
        </Link>
      </Typography>
      <DropZone component="label">
        Drag & Drop or Upload Bulk Data (.csv or .xlsx)
        <input
          type="file"
          accept=".csv, .xlsx"
          hidden
          onChange={handleFileUpload}
        />
      </DropZone>

      {(validData.length > 0 || invalidCount > 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ✅ Valid Entries: {validData.length}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ❌ Invalid Entries Skipped: {invalidCount}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={validData.length === 0}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default SchoolBulk;
